import { Button, Popover, Radio, Switch, Tooltip } from 'antd';
import { get, isEmpty, uniq } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { AiTextarea, BasicButton } from '../../../../../assets/common';
import { expand_fullfillment, mobility_description, sp_card_setting_dark } from '../../../../../assets/img';
import { talentReviewApi } from '../../../../../services/talentReviewApi';
import {
  MOBILITY_PLAN_CATEGORY_TYPE,
  MOBILITY_PLAN_DROP_CARD_INFO,
  MOBILITY_PLAN_TALENT_SETTINGS_DEFAULT,
  ROUTE_BACK_HISTORY,
} from '../../../../../utils/constants';
// import { getMaxLengthFiledInArrayMobilityPlan } from '../../../../../utils/helper';
import PositionItem from '../../../../Meeting/AgendaDetails/components/Recommendations/wrappers/PositionItem';
import TalentReviewCollapse from '../../TalentReviewCollapse/TalentReviewCollapse';
import ConfirmDelete from '../ConfirmDelete/ConfirmDelete';
import DropCardRecommendation from '../DropCardRecommendation/DropCardRecommendation';
import Fullfillment from '../Fullfillment/Fullfillment';
import RecommendationTableMobilityPlan from '../RecommendationTableForMobilityPlan/RecommendationsModilityPlan';
import SelectPositionMobilityPlan from '../SelectPositionMobilityPlan/SelectPositionMobilityPlan';
import * as styles from './mobility-form.module.scss';

import { arrayMove, checkNoInput, insertAtIndex, removeAtIndex } from '../../../../../utils/helper';
import MobilityItem from '../../../../Meeting/AgendaDetails/components/Recommendations/wrappers/MobilityItem';
import PositionMobilityPlanDetailSelection from '../PositionMobilityPlanDetailSelection/PositionMobilityPlanDetailSelection';
import useMaxHeightChildElement from '../../../../../hooks/useMaxHeightChildElement';

const MOBILITY_PLAN_TYPE = [
  { label: 'Next Mobility', fieldValue: 'next_mobility' },
  { label: 'Next 2 Mobility', fieldValue: 'next_2_mobility' },
  { label: 'End State', fieldValue: 'end_state' },
];

// Add new indicators here — each key maps to a flag in the indicatorVisibility object
const INDICATOR_TOGGLES = [
  { key: 'aspiration', label: 'Talent Aspiration Indicator' },
  { key: 'development_plan', label: 'Development Plan Indicator' },
];

// indicatorVisibility keys map to talent_settings flags persisted on each mobility_plan item
const getIndicatorVisibilityFromTalentSettings = (talentSettings) => ({
  aspiration: talentSettings?.talent_aspiration ?? false,
  development_plan: talentSettings?.development_plan ?? false,
});

const MobilityForm = (props) => {
  const {
    onlyViewTalentAgenda,
    index,
    item,
    isTheLast,
    lengthMobility,
    isPrinting,
    setAddTalent,
    currentStaffId,
    setFormMobilityPlan,
    mobilitySettings,
    setMobilitySettings,
    addTalent,
    setMode,
    agendaId,
  } = props;

  const [data, setData] = useState(item);
  const [openDelete, setOpenDelete] = useState(null);
  const [positionSelected, setPositionSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEndState, setLoadingEndState] = useState(false);
  const [endStateRecommendation, setEndStateRecommendation] = useState([]);
  const [nextmobilityRecommendation, setNextMobilityRecommendation] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(MOBILITY_PLAN_CATEGORY_TYPE[0].value);
  const [currentManualValue, setCurrentManualValue] = useState({
    next_mobility: data?.next_mobility?.manual,
    next_2_mobility: data?.next_2_mobility?.manual,
    end_state: data?.end_state?.manual,
  });
  const [open, setOpen] = useState(false);
  const [indicatorVisibility, setIndicatorVisibility] = useState(() =>
    getIndicatorVisibilityFromTalentSettings(item?.talent_settings)
  );
  const [pendingIndicatorVisibility, setPendingIndicatorVisibility] = useState(() =>
    getIndicatorVisibilityFromTalentSettings(item?.talent_settings)
  );
  const [indicatorToggleOpen, setIndicatorToggleOpen] = useState(false);

  const roleActive = useSelector((state) => state.user.roleActive);

  const handleApplyIndicatorVisibility = () => {
    setIndicatorVisibility(pendingIndicatorVisibility);
    setIndicatorToggleOpen(false);
    const talentSettings = {
      ...MOBILITY_PLAN_TALENT_SETTINGS_DEFAULT,
      ...item.talent_settings,
      talent_aspiration: pendingIndicatorVisibility.aspiration,
      development_plan: pendingIndicatorVisibility.development_plan,
    };
    setAddTalent((prev) => ({
      ...prev,
      mobility_plan: prev.mobility_plan.map((plan) =>
        plan.idx === item.idx
          ? {
              ...plan,
              talent_settings: {
                ...MOBILITY_PLAN_TALENT_SETTINGS_DEFAULT,
                ...plan.talent_settings,
                talent_aspiration: pendingIndicatorVisibility.aspiration,
                development_plan: pendingIndicatorVisibility.development_plan,
              },
            }
          : plan
      ),
    }));
    // Persist immediately for a saved agenda; for a new (unsaved) agenda it stays in addTalent for the draft save
    if (agendaId && !isNaN(Number(agendaId))) {
      talentReviewApi
        .updateMobilityPlanTalentSettings(
          {
            agenda_id: agendaId,
            version: addTalent?.top_talent?.version || 0,
            staff_id: addTalent?.top_talent?.staff_id || null,
            plan: item.plan,
            talent_settings: talentSettings,
          },
          roleActive.roleId
        )
        .catch(console.error);
    }
  };

  useEffect(() => {
    setData(item);
  }, [item.end_state, item.next_mobility, item.next_2_mobility, item.mode, item.idx]);

  // Keep indicator visibility in sync with the saved talent_settings without resetting the form data
  useEffect(() => {
    setIndicatorVisibility(getIndicatorVisibilityFromTalentSettings(item?.talent_settings));
  }, [item.talent_settings]);

  const onlyViewTalent = useMemo(() => {
    const pageHistory = new URLSearchParams(window.location.search).get('history');
    return pageHistory === ROUTE_BACK_HISTORY.SP_DASHBOARD;
  }, []);

  const onChangeCategoryType = (e) => {
    setCurrentCategory(e.target.value);
  };

  const fetchNextMobilityRecommendation = useCallback(async () => {
    const params = {
      staff_id: currentStaffId,
      position_code: !isEmpty(data.end_state.selection) ? data.end_state.selection[0].position_code : null,
      category: currentCategory,
    };
    if (!params?.position_code || isNaN(Number(params?.staff_id)) || isNaN(Number(params?.position_code))) return setNextMobilityRecommendation([]);
    try {
      setLoading(true);
      const res = await talentReviewApi.getNextMobilityRecommendation({
        ...params,
        staff_id: Number(params?.staff_id),
        position_code: Number(params?.position_code),
      });
      const mobilityRecommendation = get(res, 'data.mobilityRecommendation') || [];

      const alignBody = {
        mobilityRecommendation: mobilityRecommendation.map((item) => ({
          mobility_code: item.mobility_code,
          mobility_name: item.mobility_name,
          business: item.business,
          function: item.function,
          opu: item.opu,
        })),
      };
      const alignRes = await talentReviewApi.alignNextMobilityWithTalent(currentStaffId, alignBody);
      const alignedMobilityRecommendation = get(alignRes, 'data.result.mobilityRecommendation') || [];
      const aspirationMap = new Map(alignedMobilityRecommendation.map((item) => [Number(item.mobility_code), item.isAspiration]));
      const developmentPlanMap = new Map(alignedMobilityRecommendation.map((item) => [Number(item.mobility_code), item.isDevelopmentPlan]));

      setNextMobilityRecommendation(
        mobilityRecommendation.map((item) => ({
          ...item,
          isAspiration: aspirationMap.get(Number(item.mobility_code)) || 0,
          isDevelopmentPlan: developmentPlanMap.get(Number(item.mobility_code)) || 0,
        }))
      );
    } catch (error) {
      console.error(error);
      setNextMobilityRecommendation([]);
    } finally {
      setLoading(false);
    }
  }, [data.end_state.selection, currentStaffId, currentCategory]);

  // Get End State Recommendation Table
  useEffect(() => {
    if (!currentStaffId) return;
    const fetchEndStateRecommendation = async () => {
      try {
        setLoadingEndState(true);
        const res = await talentReviewApi.getEndStateRecommendation(currentStaffId);
        if (res.status === 200) {
          setEndStateRecommendation(res?.data?.result || []);
          setAddTalent((prev) => {
            if (!prev.end_state_recommendation) {
              return { ...prev, end_state_recommendation: res?.data?.result || [] };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingEndState(false);
      }
    };
    fetchEndStateRecommendation();
  }, [currentStaffId, setAddTalent]);

  useEffect(() => {
    setCurrentManualValue({
      next_mobility: data?.next_mobility?.manual,
      next_2_mobility: data?.next_2_mobility?.manual,
      end_state: data?.end_state?.manual,
    });
  }, [data]);

  // Get Next Mobility Recommendation Table
  useEffect(() => {
    fetchNextMobilityRecommendation();
  }, [data.end_state.selection, currentCategory, fetchNextMobilityRecommendation]);

  const handleOnChangeManual = (e, type) => {
    const value = e?.target?.value;
    setData((prev) => {
      return {
        ...prev,
        [type]: {
          ...prev[type],
          manual: value,
        },
      };
    });
  };

  const handleEdit = (idx) => {
    setAddTalent((prev) => {
      return {
        ...prev,
        mobility_plan: !isEmpty(prev?.mobility_plan)
          ? prev.mobility_plan.map((item) => {
              if (item.idx === idx) {
                return {
                  ...item,
                  next_mobility: data.next_mobility,
                  next_2_mobility: data.next_2_mobility,
                  end_state: data.end_state,
                  mode: 'view',
                };
              }
              return item;
            })
          : prev?.mobility_plan,
      };
    });
    updateStatusForm(idx);
  };

  const handleRemove = (idx) => {
    setAddTalent((prev) => {
      return {
        ...prev,
        mobility_plan: prev.mobility_plan.filter((item) => item.idx !== idx),
      };
    });
    updateStatusForm(idx);
  };

  const updateStatusForm = (removeIdx) =>
    removeIdx ? setFormMobilityPlan((prev) => [...prev].filter((v) => v !== removeIdx)) : setFormMobilityPlan((prev) => uniq([...prev, data.idx]));

  // Drag drop state
  const [draggingItem, setDraggingItem] = useState(null);
  const [dragItem, setDragItem] = useState({});

  const handleDragStart = (event) => {
    const data = event.active.data.current;

    setDraggingItem(event.active.id);
    if (data) {
      setDragItem(data);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over) {
      return;
    }

    if (!active.data.current.sortable) return;
    const activeContainer = active.data.current.sortable.containerId;
    const overContainer = over.data.current?.sortable.containerId || over.id;

    if (!overContainer) return;
    const convertOverContainer = overContainer.includes('default') ? overContainer.replace('_default', '') : overContainer;
    const isExist = data[convertOverContainer]?.selection.find((item) => Number(item.position_code) === Number(active.data.current.position_code));
    if (isExist && activeContainer !== convertOverContainer) return;

    if (activeContainer === 'recommendation') {
      if (convertOverContainer === 'end_state') {
        setData((prev) => ({
          ...prev,
          [convertOverContainer]: {
            ...prev[convertOverContainer],
            selection: [{ ...active.data.current, sortable: { containerId: convertOverContainer } }],
          },
        }));
      }
      if (convertOverContainer !== 'end_state') {
        const concatArr = [
          ...(!isEmpty(data?.next_mobility?.selection) ? data.next_mobility.selection : []),
          ...(!isEmpty(data?.next_2_mobility?.selection) ? data.next_2_mobility.selection : []),
        ].map((el) => el?.priority || 0);
        const isCurrentPriority = isEmpty(concatArr) ? 1 : Math.max(...concatArr) + 1;

        setData((prev) => ({
          ...prev,
          [convertOverContainer]: {
            ...prev[convertOverContainer],
            selection: [
              ...(prev[convertOverContainer]?.selection || []),
              {
                ...active.data.current,
                priority: isCurrentPriority,
                sortable: { containerId: convertOverContainer },
              },
            ],
          },
        }));
      }
    }

    if (activeContainer !== 'recommendation') {
      if (active.id === over.id) return;
      if (activeContainer === 'end_state') return;
      if (convertOverContainer === 'end_state') return;
      const convertActiveId = active.id.includes('next_mobility')
        ? active.id.replace('next_mobility', '')
        : active.id.includes('next_2_mobility')
        ? active.id.replace('next_2_mobility', '')
        : active.id;
      const convertOverId = over.id.includes('next_mobility')
        ? over.id.replace('next_mobility', '')
        : over.id.includes('next_2_mobility')
        ? over.id.replace('next_2_mobility', '')
        : over.id;
      const activeIndex = data[activeContainer]?.selection.findIndex((item) => Number(item.position_code) === Number(convertActiveId));
      const overIndex = data[convertOverContainer]?.selection.findIndex((item) => Number(item.position_code) === Number(convertOverId));

      setData((items) => {
        let newItems;
        if (activeContainer === convertOverContainer) {
          newItems = {
            ...items,
            [convertOverContainer]: {
              ...items[convertOverContainer],
              selection: arrayMove(items[convertOverContainer]?.selection, activeIndex, overIndex),
            },
          };
        } else {
          newItems = moveBetweenContainers(items, activeContainer, activeIndex, convertOverContainer, overIndex, active.data.current);
        }

        return newItems;
      });
    }
  };

  const moveBetweenContainers = (items, activeContainer, activeIndex, convertOverContainer, overIndex, item) => {
    return {
      ...items,
      [activeContainer]: {
        ...items[activeContainer],
        selection: removeAtIndex(items[activeContainer]?.selection || [], activeIndex),
      },
      [convertOverContainer]: {
        ...items[convertOverContainer],
        selection: insertAtIndex(
          items[convertOverContainer]?.selection || [],
          overIndex === -1 ? items[convertOverContainer]?.selection.length : overIndex,
          {
            ...item,
            sortable: { containerId: convertOverContainer },
          }
        ),
      },
    };
  };

  // const maxLenghtCharactersNM = getMaxLengthFiledInArrayMobilityPlan(data.next_mobility?.selection);
  // const maxLenghtCharactersN2M = getMaxLengthFiledInArrayMobilityPlan(data.next_2_mobility?.selection);
  // const maxLenghtCharactersES = getMaxLengthFiledInArrayMobilityPlan(data.end_state?.selection);
  const maxHeight = useMaxHeightChildElement('mobility-form', `sortable-item-`, data, open, mobilitySettings);

  return (
    <>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} autoScroll={{ enabled: true }}>
        <div style={{ gap: '10px' }} className={`d-flex ${styles.formWrapper}`}>
          <div className="w-100" style={{ borderTop: '3px solid #3f3c4c' }}>
            <div className="position-relative">
              <div className={styles.separatedLabel} style={{ right: open ? '279px' : '362px' }} />
              <div className={`${styles.labelRow} ${onlyViewTalentAgenda ? styles.labelRowCustom : ''}`}>
                <label htmlFor="next-mobility">{MOBILITY_PLAN_TYPE[0].label}</label>
                <label htmlFor="next-2-mobility">{MOBILITY_PLAN_TYPE[1].label}</label>
                <label htmlFor="end-state">{MOBILITY_PLAN_TYPE[2].label}</label>
                <div className={styles.setting}>
                  {!isPrinting && (
                    <PositionMobilityPlanDetailSelection
                      mobilitySettings={mobilitySettings}
                      setMobilitySettings={setMobilitySettings}
                      mobility={item}
                      addTalent={addTalent}
                    />
                  )}
                </div>
                <div className={styles.title}>{!isPrinting ? `Plan ${index + 1}` : `Mobility Plan ${index + 1}`}</div>
              </div>
            </div>
            <div className="position-relative">
              <div className={styles.separatedContent} style={{ right: open ? '279px' : '362px' }} />
              <div className={`${styles.inputRow} ${!isTheLast && !isPrinting ? styles.inputRowBorder : ''}`}>
                {['edit'].includes(data.mode) && !onlyViewTalentAgenda && (
                  <div className={styles.inputWrapper} id="mobility-form">
                    <div className={styles.inputCell}>
                      <SelectPositionMobilityPlan
                        sectionIndex={index}
                        data={data}
                        setData={setData}
                        type={MOBILITY_PLAN_TYPE[0].fieldValue}
                        typeAnother={MOBILITY_PLAN_TYPE[1].fieldValue}
                        // maxLength={Math.max(maxLenghtCharactersNM, maxLenghtCharactersN2M, maxLenghtCharactersES)}
                        // isOpenFulfilMent={open}
                        setPositionSelected={setPositionSelected}
                        positionSelected={positionSelected}
                        maxHeight={maxHeight}
                        mobilitySettings={mobilitySettings}
                        updateStatusForm={updateStatusForm}
                      />
                      <DropCardRecommendation
                        cardInfo={{
                          ...MOBILITY_PLAN_DROP_CARD_INFO.NEXT_MOBILITY,
                          desc: `Drag the card from Recommendation table for Plan ${index + 1} Next Mobility and Next 2 Mobility to Add Position`,
                        }}
                        sectionIndex={index}
                        dropId="next_mobility_default"
                      />
                      <AiTextarea
                        rows={3}
                        style={data.mode === 'view' || onlyViewTalentAgenda ? { marginBottom: 0 } : { marginBottom: '10px' }}
                        className={styles.textareaField}
                        placeholder="Enter Next Mobility"
                        name="next_mobility"
                        id="next-mobility"
                        data-testid="next-mobility"
                        onChange={(e) => {
                          setCurrentManualValue((prev) => {
                            return {
                              ...prev,
                              next_mobility: e?.target?.value,
                            };
                          });
                          handleOnChangeManual(e, MOBILITY_PLAN_TYPE[0].fieldValue);
                          updateStatusForm();
                        }}
                        value={currentManualValue.next_mobility || null}
                      />
                    </div>
                    <div className={styles.inputCell}>
                      <SelectPositionMobilityPlan
                        sectionIndex={index}
                        data={data}
                        setData={setData}
                        type={MOBILITY_PLAN_TYPE[1].fieldValue}
                        typeAnother={MOBILITY_PLAN_TYPE[0].fieldValue}
                        // maxLength={Math.max(maxLenghtCharactersNM, maxLenghtCharactersN2M, maxLenghtCharactersES)}
                        // isOpenFulfilMent={open}
                        setPositionSelected={setPositionSelected}
                        positionSelected={positionSelected}
                        maxHeight={maxHeight}
                        mobilitySettings={mobilitySettings}
                        updateStatusForm={updateStatusForm}
                      />
                      <DropCardRecommendation
                        cardInfo={{
                          ...MOBILITY_PLAN_DROP_CARD_INFO.NEXT_2_MOBILITY,
                          desc: `Drag the card from Recommendation table for Plan ${index + 1} Next Mobility and Next 2 Mobility to Add Position`,
                        }}
                        sectionIndex={index}
                        dropId="next_2_mobility_default"
                      />
                      <AiTextarea
                        rows={3}
                        style={data.mode === 'view' || onlyViewTalentAgenda ? { marginBottom: 0 } : { marginBottom: '10px' }}
                        className={styles.textareaField}
                        placeholder="Enter Next 2 Mobility"
                        name="next_2_mobility"
                        id="next-2-mobility"
                        data-testid="next-2-mobility"
                        onChange={(e) => {
                          setCurrentManualValue((prev) => {
                            return {
                              ...prev,
                              next_2_mobility: e?.target?.value,
                            };
                          });
                          handleOnChangeManual(e, MOBILITY_PLAN_TYPE[1].fieldValue);
                          updateStatusForm();
                        }}
                        value={currentManualValue.next_2_mobility || null}
                      />
                    </div>
                    <div className={styles.inputCell}>
                      <SelectPositionMobilityPlan
                        sectionIndex={index}
                        data={data}
                        setData={setData}
                        type={MOBILITY_PLAN_TYPE[2].fieldValue}
                        // maxLength={Math.max(maxLenghtCharactersNM, maxLenghtCharactersN2M, maxLenghtCharactersES)}
                        // isOpenFulfilMent={open}
                        maxHeight={maxHeight}
                        mobilitySettings={mobilitySettings}
                        updateStatusForm={updateStatusForm}
                      />
                      {isEmpty(data.end_state.selection) && (
                        <DropCardRecommendation
                          cardInfo={{
                            ...MOBILITY_PLAN_DROP_CARD_INFO.END_STATE,
                            desc: `Drag the card from Recommendation table for Plan ${index + 1} End State to Add Position`,
                          }}
                          sectionIndex={index}
                          dropId="end_state_default"
                        />
                      )}
                      <AiTextarea
                        rows={3}
                        style={data.mode === 'view' || onlyViewTalentAgenda ? { marginBottom: 0 } : { marginBottom: '10px' }}
                        className={styles.textareaField}
                        placeholder="Enter End State"
                        name="end_state"
                        id="end-state"
                        data-testid="end-state"
                        onChange={(e) => {
                          setCurrentManualValue((prev) => {
                            return {
                              ...prev,
                              end_state: e?.target?.value,
                            };
                          });
                          handleOnChangeManual(e, MOBILITY_PLAN_TYPE[2].fieldValue);
                          updateStatusForm();
                        }}
                        value={currentManualValue.end_state || null}
                      />
                    </div>
                  </div>
                )}
                {(data.mode === 'view' || onlyViewTalentAgenda) && (
                  <div className={styles.inputWrapper}>
                    <div
                      className={styles.viewCell}
                      style={
                        !isPrinting ? { backgroundColor: '#EDF1F8', borderColor: '#9fb0c2' } : { backgroundColor: '#ffffff', borderColor: '#ffffff' }
                      }
                    >
                      <SelectPositionMobilityPlan
                        sectionIndex={index}
                        isView
                        data={data}
                        type={MOBILITY_PLAN_TYPE[0].fieldValue}
                        // maxLength={Math.max(maxLenghtCharactersNM, maxLenghtCharactersN2M, maxLenghtCharactersES)}
                        // isOpenFulfilMent={open}
                        setPositionSelected={setPositionSelected}
                        positionSelected={positionSelected}
                        maxHeight={maxHeight}
                        mobilitySettings={mobilitySettings}
                      />
                      <div className={styles.textareaField}>{data?.next_mobility?.manual}</div>
                    </div>
                    <div
                      className={styles.viewCell}
                      style={
                        !isPrinting ? { backgroundColor: '#EDF1F8', borderColor: '#9FB0C2' } : { backgroundColor: '#ffffff', borderColor: '#ffffff' }
                      }
                    >
                      <SelectPositionMobilityPlan
                        sectionIndex={index}
                        isView
                        data={data}
                        type={MOBILITY_PLAN_TYPE[1].fieldValue}
                        // maxLength={Math.max(maxLenghtCharactersNM, maxLenghtCharactersN2M, maxLenghtCharactersES)}
                        // isOpenFulfilMent={open}
                        setPositionSelected={setPositionSelected}
                        positionSelected={positionSelected}
                        maxHeight={maxHeight}
                        mobilitySettings={mobilitySettings}
                      />
                      <div className={styles.textareaField}>{data?.next_2_mobility?.manual}</div>
                    </div>
                    <div
                      className={styles.viewCell}
                      style={
                        !isPrinting ? { backgroundColor: '#EDF1F8', borderColor: '#9FB0C2' } : { backgroundColor: '#ffffff', borderColor: '#ffffff' }
                      }
                    >
                      <SelectPositionMobilityPlan
                        sectionIndex={index}
                        isView
                        data={data}
                        type={MOBILITY_PLAN_TYPE[2].fieldValue}
                        // maxLength={Math.max(maxLenghtCharactersNM, maxLenghtCharactersN2M, maxLenghtCharactersES)}
                        // isOpenFulfilMent={open}
                        maxHeight={maxHeight}
                        mobilitySettings={mobilitySettings}
                      />
                      <div className={styles.textareaField}>{data?.end_state?.manual}</div>
                    </div>
                  </div>
                )}
                {!onlyViewTalentAgenda && (
                  <div className={`d-flex justify-content-between`}>
                    <div>
                      {lengthMobility !== 1 && (
                        <BasicButton mode="remove" onClick={() => setOpenDelete(true)} style={{ width: '120px' }}>
                          Remove Plan
                        </BasicButton>
                      )}
                    </div>
                    <div>
                      {data.mode === 'view' && (
                        <BasicButton
                          mode="teal"
                          onClick={() => {
                            setMode('edit');
                            updateStatusForm();
                          }}
                          style={{ width: '100px', marginLeft: '15px' }}
                        >
                          Edit
                        </BasicButton>
                      )}
                      {['edit'].includes(data.mode) && (
                        <>
                          <BasicButton
                            mode="border-teal"
                            onClick={() => {
                              setData(item);
                              setFormMobilityPlan((prev) =>
                                [...prev].find((v) => v === index + 1) ? [...prev].filter((v) => v !== index + 1) : [...prev]
                              );
                              !checkNoInput(item) && setMode('view');
                            }}
                            style={{ width: '100px', marginLeft: '15px' }}
                            disabled={checkNoInput(data)}
                          >
                            Cancel
                          </BasicButton>
                          <BasicButton
                            mode="teal"
                            onClick={() => handleEdit(data.idx)}
                            style={{ width: '100px', marginLeft: '15px' }}
                            disabled={checkNoInput(data)}
                          >
                            Save
                          </BasicButton>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recommendation Category */}
            {!onlyViewTalent && !isPrinting && (
              <div className={styles.recommendationCategoryWrapper}>
                <TalentReviewCollapse
                  header={
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <h3 className={styles.recommendationCategoryTitle}>Recommendations Category</h3>
                      <Popover
                        trigger="click"
                        visible={indicatorToggleOpen}
                        onVisibleChange={(visible) => {
                          setIndicatorToggleOpen(visible);
                          if (visible) setPendingIndicatorVisibility(indicatorVisibility);
                        }}
                        placement="rightTop"
                        content={
                          <div style={{ padding: '4px 8px' }}>
                            <div style={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, marginBottom: '24px', color: '#67798a' }}>Show</div>
                            {INDICATOR_TOGGLES.map(({ key, label }) => (
                              <div
                                key={key}
                                className="d-flex justify-content-between align-items-center"
                                style={{ gap: '12px', marginBottom: '24px' }}
                              >
                                <Switch
                                  checked={pendingIndicatorVisibility[key]}
                                  onChange={(checked) => setPendingIndicatorVisibility((prev) => ({ ...prev, [key]: checked }))}
                                  style={{ backgroundColor: `${pendingIndicatorVisibility[key] ? '#009089' : ''}` }}
                                />
                                <span style={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 600 }}>{label}</span>
                              </div>
                            ))}
                              <Button style={{ background: '#00a19c', width: '100%', height: '36px', borderRadius: '6px' }} type="primary" size="small" onClick={handleApplyIndicatorVisibility}>
                                Apply
                              </Button>
                          </div>
                        }
                      >
                        <div
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={() => {}}
                          role="button"
                          aria-label="Toggle talent aspiration indicator"
                          title="Talent Aspiration Indicator"
                          style={{
                            width: '39px',
                            height: '39px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: 'none',
                            background: '#d6d6d6',
                            cursor: 'pointer',
                            position: 'absolute',
                            right: '47px',
                            top: 0,
                          }}
                        >
                          <img
                            src={sp_card_setting_dark}
                            alt="talent aspiration indicator setting"
                            style={{
                              height: '20px',
                            }}
                          />
                        </div>
                      </Popover>
                    </div>
                  }
                  isCollapsed={onlyViewTalentAgenda}
                >
                  <div className={styles.recommendationCategory}>
                    <div>
                      <Radio.Group
                        onChange={onChangeCategoryType}
                        value={currentCategory}
                        style={{ marginBottom: '12px', gap: '32px' }}
                        className="d-flex justify-content-start align-items-center"
                      >
                        {MOBILITY_PLAN_CATEGORY_TYPE.map((category) => {
                          return (
                            <Radio value={category.value} className={styles.customLabel} key={category.value}>
                              <div className="d-flex justify-content-start align-items-center" style={{ gap: '5px' }}>
                                {category.name}{' '}
                                <Tooltip
                                  placement="top"
                                  title={<div>{category.desc}</div>}
                                  color={'black'}
                                  key={'black'}
                                  overlayInnerStyle={{ borderRadius: '6px', width: '400px' }}
                                >
                                  <span>
                                    <img src={mobility_description} alt="mobility_description" />
                                  </span>
                                </Tooltip>
                              </div>
                            </Radio>
                          );
                        })}
                      </Radio.Group>
                    </div>

                    <div className={styles.recommendationCategoryContent}>
                      <RecommendationTableMobilityPlan
                        recommendationList={
                          !isEmpty(nextmobilityRecommendation)
                            ? nextmobilityRecommendation.map((item) => ({
                                ...item,
                                position_code: item.mobility_code,
                                position_name: item.mobility_name,
                              }))
                            : []
                        }
                        setRecommendationList={() => {}}
                        tableName={`Recommendations for Plan ${index + 1} : Next Mobility and Next 2 Mobility`}
                        type={MOBILITY_PLAN_DROP_CARD_INFO.NEXT_MOBILITY.type}
                        tableIndex={index}
                        loading={loading}
                        indicatorVisibility={indicatorVisibility}
                      />
                      <RecommendationTableMobilityPlan
                        recommendationList={endStateRecommendation}
                        setRecommendationList={() => {}}
                        tableName={`Recommendations for Plan ${index + 1} : End State`}
                        type={MOBILITY_PLAN_DROP_CARD_INFO.END_STATE.type}
                        tableIndex={index}
                        loading={loadingEndState}
                        indicatorVisibility={indicatorVisibility}
                      />
                    </div>
                    <DragOverlay
                      dropAnimation={{
                        duration: 500,
                      }}
                    >
                      {draggingItem ? (
                        <div
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #bdb6ea',
                            width: dragItem.isMobility ? '320px' : '450px',
                          }}
                        >
                          {dragItem.isMobility ? (
                            <MobilityItem item={dragItem} index={dragItem.index} draggingItem={draggingItem} />
                          ) : (
                            <PositionItem item={dragItem} index={dragItem.index} draggingItem={draggingItem} />
                          )}
                        </div>
                      ) : null}
                    </DragOverlay>
                  </div>
                </TalentReviewCollapse>
              </div>
            )}
          </div>
          {!onlyViewTalent && !isPrinting && (
            <div>
              <div
                onClick={() => setOpen(!open)}
                onKeyDown={() => {}}
                aria-controls="example-collapse-text"
                aria-expanded={open}
                style={{
                  width: open ? '280px' : '30px',
                  height: '56px',
                  transition: 'width 250ms',
                  background: '#677C91',
                  display: 'flex',
                  justifyContent: open ? 'flex-start' : 'center',
                  alignItems: 'center',
                  gap: '15px',
                  border: 'none',
                  padding: '0 15px',
                  cursor: 'pointer',
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                }}
              >
                <img
                  src={expand_fullfillment}
                  alt="expand_fullfillment"
                  style={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 250ms',
                  }}
                />
                {open && (
                  <span
                    style={{
                      fontFamily: 'Inter',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: '700',
                      lineHeight: '20px',
                      letterSpacing: '0.173px',
                    }}
                  >
                    Criteria Fulfilment
                  </span>
                )}
              </div>
              <div style={{ minHeight: '210px' }}>
                <Collapse in={open} dimension="width">
                  <div id="example-collapse-text">
                    <Fullfillment
                      staffId={currentStaffId}
                      data={data}
                      positionSelected={positionSelected}
                      agendaId={agendaId}
                      version={addTalent?.top_talent?.version || 0}
                      plan={data.plan}
                    />
                  </div>
                </Collapse>
              </div>
            </div>
          )}
        </div>
        {openDelete && (
          <ConfirmDelete
            info={{
              type: 'confirmToDelete',
              visible: openDelete,
              setVisible: setOpenDelete,
              handleSubmit: () => {
                handleRemove(data.idx);
                setOpenDelete(null);
              },
              onClose: () => setOpenDelete(null),
              title: `Are you sure you want to delete “Plan ${index + 1}” from Mobility Plan ?`,
              desc: `You can't undo this action.`,
            }}
          />
        )}
      </DndContext>
    </>
  );
};

MobilityForm.propTypes = {
  onlyViewTalentAgenda: PropTypes.bool,
  index: PropTypes.number,
  item: PropTypes.object,
  setAddTalent: PropTypes.func,
  isTheLast: PropTypes.bool,
  lengthMobility: PropTypes.number,
  isPrinting: PropTypes.bool,
  positionOptions: PropTypes.array,
  agendaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default MobilityForm;
